import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Briefcase, Search, Plus, X, Clock, Trash2, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function WorkplaceManager({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', default_start: '07:30', default_end: '16:00', color: '#C8B6FF' });
  const save = async () => {
    if (!form.name.trim()) { toast.error('Namn krävs'); return; }
    try {
      await axios.post(`${API}/workplaces`, form, { withCredentials: true });
      toast.success('Mottagning sparad ✨');
      onCreated();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Kunde inte spara'); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="neo-card bg-white w-full max-w-sm p-4" onClick={(e) => e.stopPropagation()} data-testid="workplace-manager">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-['Fredoka'] font-bold text-lg">Ny mottagning</h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="T.ex. Urologmottagningen" className="neo-input w-full text-sm" data-testid="workplace-name-input" autoFocus />
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[11px] font-bold">Standard start</span>
              <input type="time" value={form.default_start} onChange={(e) => setForm({ ...form, default_start: e.target.value })} className="neo-input w-full text-sm" data-testid="workplace-start-input" />
            </label>
            <label className="block">
              <span className="text-[11px] font-bold">Standard slut</span>
              <input type="time" value={form.default_end} onChange={(e) => setForm({ ...form, default_end: e.target.value })} className="neo-input w-full text-sm" data-testid="workplace-end-input" />
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] font-bold">Färg</span>
            <div className="flex gap-1 mt-1 flex-wrap">
              {['#C8B6FF', '#FFD6E8', '#B9F5D8', '#A2D2FF', '#FFE4B5', '#FFC8DD', '#E8D5FF', '#B5EAD7'].map((c) => (
                <button key={c} onClick={() => setForm({ ...form, color: c })}
                  className={`w-7 h-7 rounded-full border-2 ${form.color === c ? 'border-black ring-2 ring-black' : 'border-black/40'}`}
                  style={{ backgroundColor: c }} aria-label={c} />
              ))}
            </div>
          </label>
          <button onClick={save} className="neo-btn w-full py-2 bg-[#B9FBC0] font-bold text-sm" data-testid="workplace-save">Spara</button>
        </div>
      </div>
    </div>
  );
}

export default function WorkShiftQuickAdd({ initialDate, onClose, onShiftCreated }) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(initialDate || new Date().toISOString().slice(0, 10));
  const [selected, setSelected] = useState(null);
  const [startTime, setStartTime] = useState('07:30');
  const [endTime, setEndTime] = useState('16:00');
  const [creating, setCreating] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/workplaces${q ? `?q=${encodeURIComponent(q)}` : ''}`, { withCredentials: true });
      setItems(data.items || []);
    } catch { toast.error('Kunde inte hämta mottagningar'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => load(query), 220);
    return () => clearTimeout(t);
  }, [query, load]);

  const pick = (wp) => {
    setSelected(wp);
    setStartTime(wp.default_start || '07:30');
    setEndTime(wp.default_end || '16:00');
  };

  const createShift = async () => {
    if (!selected) return;
    setCreating(true);
    try {
      await axios.post(`${API}/workplaces/shift`, {
        workplace_id: selected.id,
        date,
        start: startTime,
        end: endTime,
      }, { withCredentials: true });
      toast.success(`✅ ${selected.name} · ${date}`);
      onShiftCreated?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Kunde inte skapa pass'); }
    finally { setCreating(false); }
  };

  const deleteWp = async (wp, e) => {
    e.stopPropagation();
    if (!window.confirm(`Ta bort "${wp.name}" från dina mottagningar?`)) return;
    try {
      await axios.delete(`${API}/workplaces/${wp.id}`, { withCredentials: true });
      if (selected?.id === wp.id) setSelected(null);
      load(query);
      toast.success('Borttagen');
    } catch { toast.error('Kunde inte ta bort'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="neo-card bg-white w-full max-w-md max-h-[90vh] overflow-y-auto p-4" onClick={(e) => e.stopPropagation()} data-testid="workshift-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-['Fredoka'] font-bold text-lg flex items-center gap-2">
            <Briefcase className="w-5 h-5" /> Dagens jobb
          </h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="workshift-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-2">
          <label className="block">
            <span className="text-xs font-bold">Datum</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="neo-input w-full text-sm" data-testid="workshift-date" />
          </label>

          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50 pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Sök mottagning (t.ex. urolog)…"
              className="neo-input w-full text-sm pl-8"
              data-testid="workshift-search"
              autoFocus
            />
          </div>

          <div className="border-2 border-black rounded-xl max-h-[240px] overflow-y-auto bg-[#FFFAF0]" data-testid="workshift-list">
            {loading ? (
              <p className="p-4 text-xs opacity-60 text-center italic animate-pulse-soft">Laddar…</p>
            ) : items.length === 0 ? (
              <p className="p-4 text-xs opacity-60 text-center italic">Inga träffar</p>
            ) : items.map((wp) => {
              const active = selected?.id === wp.id;
              return (
                <button
                  key={wp.id}
                  onClick={() => pick(wp)}
                  className={`w-full text-left px-3 py-2 border-b border-black/20 last:border-b-0 flex items-center gap-2 transition-colors ${active ? 'bg-[#B9FBC0]' : 'hover:bg-white'}`}
                  data-testid={`workshift-item-${wp.id}`}
                >
                  <div className="w-4 h-4 rounded-full border-2 border-black flex-shrink-0" style={{ backgroundColor: wp.color }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{wp.name}</p>
                    <p className="text-[10px] opacity-70 flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {wp.default_start}–{wp.default_end} {wp.use_count > 0 && <span>· använd {wp.use_count}x</span>}</p>
                  </div>
                  {active && <Check className="w-4 h-4 flex-shrink-0" />}
                  <button
                    onClick={(e) => deleteWp(wp, e)}
                    className="opacity-30 hover:opacity-100 p-1"
                    title="Ta bort"
                    data-testid={`workshift-delete-${wp.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setManagerOpen(true)}
            className="w-full text-xs underline opacity-70 hover:opacity-100 flex items-center justify-center gap-1"
            data-testid="workshift-add-new"
          >
            <Plus className="w-3 h-3" /> Lägg till ny mottagning
          </button>

          {selected && (
            <div className="neo-card p-3 bg-[#FFF3F9] mt-2" data-testid="workshift-confirm">
              <p className="text-xs font-bold mb-2">✨ {selected.name}</p>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-[11px] font-bold">Start</span>
                  <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="neo-input w-full text-sm" data-testid="workshift-start-time" />
                </label>
                <label className="block">
                  <span className="text-[11px] font-bold">Slut</span>
                  <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="neo-input w-full text-sm" data-testid="workshift-end-time" />
                </label>
              </div>
              <button
                onClick={createShift}
                disabled={creating}
                className="neo-btn w-full mt-2 py-2 bg-[#B9FBC0] font-bold text-sm"
                data-testid="workshift-create-btn"
              >
                {creating ? 'Skapar…' : `Lägg till ${date}`}
              </button>
            </div>
          )}
        </div>

        {managerOpen && <WorkplaceManager onClose={() => setManagerOpen(false)} onCreated={() => load(query)} />}
      </div>
    </div>
  );
}
